import { useCallback, useEffect, useRef, useState } from "react";
import { useAppSelector } from "@/store/store";
import {
  selectAuthError,
  selectAuthStatus,
  selectIsAuthLoading,
} from "@/selectors/auth.selectors";
import { authService } from "@/services/auth.service";
import { useRestoreCheckoutAddress } from "@/hooks/use-restore-checkout-address";
import { logAuthTokenDiag, logMenuDiag } from "@/utils/menu-load-diagnostics";
import SplashScreen from "@/components/splash-screen";
import AuthErrorScreen from "@/components/auth/AuthErrorScreen";

type AuthBootstrapGateProps = {
  children: React.ReactNode;
};

/**
 * Ensures authentication bootstrap completes before the application shell renders.
 * Rebuilds identity from GET /auth/v1/me on every cold start.
 *
 * Important: after the app has reached a terminal bootstrap state once
 * (`authenticated` | `unauthenticated`), subsequent login/register
 * (`authenticating` / `loadingProfile`) must NOT replace the tree with SplashScreen.
 * Doing so unmounts pages (e.g. Register) and races post-signup success UI.
 */
export default function AuthBootstrapGate({ children }: AuthBootstrapGateProps) {
  const status = useAppSelector(selectAuthStatus);
  const error = useAppSelector(selectAuthError);
  const isLoading = useAppSelector(selectIsAuthLoading);
  const bootstrapStarted = useRef(false);
  const [shellReady, setShellReady] = useState(false);

  useEffect(() => {
    if (bootstrapStarted.current) return;
    bootstrapStarted.current = true;
    logMenuDiag("auth_bootstrap_start");
    logAuthTokenDiag("auth_bootstrap_start");
    void Promise.resolve(authService.bootstrap()).finally(() => {
      logMenuDiag("auth_bootstrap_complete");
      logAuthTokenDiag("auth_bootstrap_complete");
    });
  }, []);

  // Declared before the shellReady effect so a restored checkout address is already in the
  // store when the application shell first renders (no post-reload preview churn).
  useRestoreCheckoutAddress();

  useEffect(() => {
    if (status === "authenticated" || status === "unauthenticated") {
      setShellReady(true);
    }
  }, [status]);

  const handleRetry = useCallback(() => {
    void Promise.resolve(authService.bootstrap());
  }, []);

  // Initial cold start only — never tear down the shell during in-app auth.
  if (!shellReady && (status === "idle" || isLoading)) {
    return <SplashScreen />;
  }

  if (!shellReady && status === "error") {
    return <AuthErrorScreen message={error} onRetry={handleRetry} />;
  }

  return <>{children}</>;
}
